/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, August 2020
 */

import React, { useCallback, useContext, useMemo } from 'react';
import { Action, FlowBlockButton as BlockModel } from '@staffino/model';
import { ButtonBlock as Component } from '@staffino/flow-controls';
import { useFormContext } from 'react-hook-form';
import FlowContext from '../flow-context';
import { ConditionEvaluator } from '../condition-evaluator';
import useBridge from '../use-bridge-hook';
import usePropertyTranslation from '../use-property-translation-hook';
import useTemplateRenderer from '../use-template-renderer';

function normalizeUrl(url: string): string {
  if (/^\w+:\/\//.test(url)) {
    return url;
  }
  return `https://${url}`;
}

function calculateAction(block: BlockModel): Action {
  if (block.action === 'link') {
    const normalizedUrl = normalizeUrl(block.url);
    return [block.action, normalizedUrl];
  }
  return [block.action];
}

type Props = {
  block: BlockModel;
};
function ButtonBlock({ block }: Props) {
  const bridge = useBridge();
  const form = useFormContext();

  const { definition, stateManager } = useContext(FlowContext);
  const evaluator = useMemo(
    () => new ConditionEvaluator(stateManager, bridge, definition, form),
    [stateManager, bridge, definition, form],
  );
  const handlePress = useCallback(() => {
    const actions = block.actions || [];
    evaluator.applyActions([...actions, calculateAction(block)]);
  }, [block, evaluator]);

  const translatedLabel = usePropertyTranslation(
    block.translations,
    'label',
    block.label,
  );
  const [label] = useTemplateRenderer(translatedLabel);
  const [url] = useTemplateRenderer(block.url);
  return (
    <Component block={{ ...block, label: label, url }} onPress={handlePress} />
  );
}

export default ButtonBlock;
